/**
 * QR SCANNER (jsQR-based)
 * - Works on desktop webcam, tablet, and phone (front/back)
 * - Optimized for small/blurred QR codes with multi-pass processing
 * - Auto-opens URLs immediately when detected
 */

document.addEventListener('DOMContentLoaded', () => {
  const scanBtn = document.getElementById('scan-btn');
  const qrModal = document.getElementById('qr-modal');
  const closeQrBtn = document.getElementById('close-qr-btn');
  const redeemInput = document.getElementById('card-code');
  const redeemSubmitBtn = document.getElementById('redeem-btn');

  let stream = null;
  let video = null;
  let canvas = null;
  let ctx = null;
  let scanLoop = null;
  let cameras = [];
  let currentCamIndex = 0;
  let scanning = false;

  const isLikelyUrl = (text) => {
    if (!text) return false;
    return /^https?:\/\//i.test(text) || /^www\./i.test(text) || /^[\w-]+\.[\w.-]+/.test(text);
  };

  const normalizeUrl = (text) => {
    if (/^https?:\/\//i.test(text)) return text;
    return `https://${text}`;
  };

  const stopScanner = async () => {
    scanning = false;
    if (scanLoop) {
      cancelAnimationFrame(scanLoop);
      scanLoop = null;
    }
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      stream = null;
    }
    video = null;
    canvas = null;
    ctx = null;
    qrModal.style.display = 'none';
  };

  const handleDecode = async (text) => {
    if (!text || !scanning) return;
    scanning = false; // prevent double handling
    await stopScanner();

    if (isLikelyUrl(text)) {
      window.location.href = normalizeUrl(text);
      return;
    }

    if (redeemInput) {
      redeemInput.value = text;
      if (redeemSubmitBtn) redeemSubmitBtn.click();
    } else {
      alert(`Code: ${text}`);
    }
  };

  const listCameras = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      cameras = devices.filter((d) => d.kind === 'videoinput');
    } catch (err) {
      console.warn('Camera enumeration failed', err);
      cameras = [];
    }
  };

  const pickBackCameraIndex = () => {
    const keywords = ['back', 'rear', 'environment', 'main'];
    const found = cameras.findIndex((c) => keywords.some((k) => (c.label || '').toLowerCase().includes(k)));
    if (found >= 0) return found;
    return cameras.length > 1 ? 1 : 0; // heuristic: second cam often is back
  };

  const startStream = async (deviceId) => {
    const constraints = deviceId
      ? { video: { deviceId: { exact: deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } } }
      : {
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        };

    return navigator.mediaDevices.getUserMedia(constraints);
  };

  const switchCamera = async () => {
    if (cameras.length <= 1) return;
    currentCamIndex = (currentCamIndex + 1) % cameras.length;
    await restartWithCamera(cameras[currentCamIndex]);
  };

  const restartWithCamera = async (cam) => {
    await stopScanner();
    await openScanner(cam);
  };

  const scanFrame = () => {
    if (!video || video.readyState !== video.HAVE_ENOUGH_DATA || !ctx) {
      scanLoop = requestAnimationFrame(scanFrame);
      return;
    }

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) {
      scanLoop = requestAnimationFrame(scanFrame);
      return;
    }

    if (canvas.width !== vw) canvas.width = vw;
    if (canvas.height !== vh) canvas.height = vh;
    ctx.drawImage(video, 0, 0, vw, vh);

    const passes = [];

    // Pass 1: full frame
    passes.push(ctx.getImageData(0, 0, vw, vh));

    // Pass 2: center crop, 2x zoom (50% area to 100%)
    const crop2 = document.createElement('canvas');
    const crop2Ctx = crop2.getContext('2d');
    const crop2Size = Math.min(vw, vh) * 0.5;
    const crop2X = (vw - crop2Size) / 2;
    const crop2Y = (vh - crop2Size) / 2;
    crop2.width = 600;
    crop2.height = 600;
    crop2Ctx.drawImage(canvas, crop2X, crop2Y, crop2Size, crop2Size, 0, 0, 600, 600);
    passes.push(crop2Ctx.getImageData(0, 0, 600, 600));

    // Pass 3: tighter center crop, 4x zoom
    const crop4 = document.createElement('canvas');
    const crop4Ctx = crop4.getContext('2d');
    const crop4Size = Math.min(vw, vh) * 0.25;
    const crop4X = (vw - crop4Size) / 2;
    const crop4Y = (vh - crop4Size) / 2;
    crop4.width = 800;
    crop4.height = 800;
    crop4Ctx.drawImage(canvas, crop4X, crop4Y, crop4Size, crop4Size, 0, 0, 800, 800);
    passes.push(crop4Ctx.getImageData(0, 0, 800, 800));

    // Pass each through jsQR with inversion attempts
    for (const img of passes) {
      const res = jsQR(img.data, img.width, img.height, { inversionAttempts: 'attemptBoth' });
      if (res && res.data) {
        handleDecode(res.data);
        return;
      }
    }

    scanLoop = requestAnimationFrame(scanFrame);
  };

  const openScanner = async (preferredCam) => {
    try {
      qrModal.style.display = 'block';

      // Build UI container content
      const qrReader = document.getElementById('qr-reader');
      qrReader.innerHTML = `
        <div style="position:relative; width:100%; background:#000; border-radius:12px; overflow:hidden;">
          <video id="qr-video" style="width:100%; max-height:70vh; object-fit:cover; display:block;" playsinline muted></video>
          <canvas id="qr-canvas" style="display:none;"></canvas>
          <div id="scan-zone" style="position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); width:min(220px,65vw); height:min(220px,65vw); border:4px solid #00ff00; border-radius:16px; box-shadow:0 0 0 9999px rgba(0,0,0,0.6);">
            <div style="position:absolute;top:-2px;left:-2px;width:26px;height:26px;border-top:4px solid #fff;border-left:4px solid #fff;border-radius:8px 0 0 0;"></div>
            <div style="position:absolute;top:-2px;right:-2px;width:26px;height:26px;border-top:4px solid #fff;border-right:4px solid #fff;border-radius:0 8px 0 0;"></div>
            <div style="position:absolute;bottom:-2px;left:-2px;width:26px;height:26px;border-bottom:4px solid #fff;border-left:4px solid #fff;border-radius:0 0 0 8px;"></div>
            <div style="position:absolute;bottom:-2px;right:-2px;width:26px;height:26px;border-bottom:4px solid #fff;border-right:4px solid #fff;border-radius:0 0 8px 0;"></div>
            <div style="position:absolute;top:0;left:12%;width:76%;height:3px;background:linear-gradient(90deg,transparent,#00ff00,transparent);animation:scanLine 2s linear infinite;"></div>
          </div>
          <button id="switch-cam" style="display:none; position:absolute; top:14px; right:14px; width:44px; height:44px; border:none; border-radius:50%; background:rgba(255,255,255,0.9); box-shadow:0 2px 8px rgba(0,0,0,0.3); cursor:pointer;">🔄</button>
          <div style="position:absolute; bottom:12px; left:0; right:0; text-align:center;">
            <span style="background:rgba(0,0,0,0.55); color:#fff; padding:6px 12px; border-radius:14px; font-size:13px;">Align the small QR inside the green box</span>
          </div>
        </div>
        <style>@keyframes scanLine { 0%{top:8%;opacity:0;} 50%{opacity:1;} 100%{top:92%;opacity:0;} }</style>
      `;

      video = document.getElementById('qr-video');
      canvas = document.getElementById('qr-canvas');
      ctx = canvas.getContext('2d', { willReadFrequently: true });

      // If we already know cameras, pick preferred; else enumerate
      if (!cameras.length) await listCameras();
      if (preferredCam) {
        currentCamIndex = cameras.findIndex((c) => c.deviceId === preferredCam.deviceId);
      } else {
        currentCamIndex = pickBackCameraIndex();
      }

      // Start stream
      const cam = cameras[currentCamIndex];
      stream = await startStream(cam?.deviceId);
      video.srcObject = stream;

      // Show switch button when multiple cameras
      const switchBtn = document.getElementById('switch-cam');
      if (cameras.length > 1) {
        switchBtn.style.display = 'block';
        switchBtn.onclick = (e) => {
          e.stopPropagation();
          switchCamera();
        };
      }

      await video.play();

      // Give autofocus/autoexposure a moment to settle
      await new Promise((r) => setTimeout(r, 300));

      scanning = true;
      scanLoop = requestAnimationFrame(scanFrame);
    } catch (err) {
      console.error('Failed to open scanner', err);
      alert('Unable to access camera. Please allow permission and try again.');
      await stopScanner();
    }
  };

  scanBtn.addEventListener('click', () => openScanner());
  closeQrBtn.addEventListener('click', stopScanner);
  window.addEventListener('click', (e) => { if (e.target === qrModal) stopScanner(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && qrModal.style.display === 'block') stopScanner(); });
});